import React, { useState, useEffect } from "react";
import PropTypes from 'prop-types';
import {
  Dropdown,
  DropdownToggle,
  DropdownMenu,
  Badge,
} from "reactstrap";

// i18n
import { withTranslation } from "react-i18next";
// Redux
import { connect } from "react-redux";
import { withRouter, Link } from "react-router-dom";

// users
import user1 from "../../../assets/images/users/avatar-1.jpg";
import cookieHelper from "helpers/getCookieData";
import { getAuthUser, getUserRole, isSuperAdminUser } from "helpers/permissionHelper";

const ProfileMenu = props => {
  const [menu, setMenu] = useState(false);
  const [userData, setUserData] = useState(null);
  const [roleName, setRoleName] = useState("Admin");
  const [clinicName, setClinicName] = useState("");

  const refreshUserInfo = () => {
    const auth = getAuthUser();
    const role = getUserRole();
    if (auth) {
      setUserData(auth);
    }
    setRoleName(role);

    try {
      const clinicCookie = cookieHelper.getCookie('_c');
      if (clinicCookie) {
        const parsedClinic = JSON.parse(atob(clinicCookie));
        if (parsedClinic?.name) {
          setClinicName(parsedClinic.name);
        }
      }
    } catch {
      // ignore
    }
  };

  useEffect(() => {
    refreshUserInfo();
  }, [props.success]);

  // Compute Display Name
  const displayName = userData?.name ||
    userData?.user_name ||
    (userData?.first_name ? `${userData.first_name} ${userData.last_name || ''}`.trim() : '') ||
    (roleName === 'admin' ? 'Administrator' : roleName.charAt(0).toUpperCase() + roleName.slice(1));

  const displayEmail = userData?.email || "";

  // Compute Role Label & Color
  const getRoleBadge = (role) => {
    switch (role?.toLowerCase()) {
      case 'admin':
      case 'superadmin':
        return { label: 'Admin', color: 'primary' };
      case 'doctor':
        return { label: 'Doctor', color: 'success' };
      case 'receptionist':
        return { label: 'Receptionist', color: 'info' };
      case 'patient':
        return { label: 'Patient', color: 'warning' };
      default:
        return { label: role?.toUpperCase() || 'User', color: 'secondary' };
    }
  };

  const roleBadge = getRoleBadge(roleName);

  // Initials for Avatar
  const getInitials = (name) => {
    if (!name) return "U";
    const parts = name.trim().split(" ");
    if (parts.length >= 2) {
      return (parts[0][0] + parts[1][0]).toUpperCase();
    }
    return name.slice(0, 2).toUpperCase();
  };

  return (
    <React.Fragment>
      <Dropdown
        isOpen={menu}
        toggle={() => setMenu(!menu)}
        className="d-inline-block"
      >
        <DropdownToggle
          className="btn header-item d-flex align-items-center"
          id="page-header-user-dropdown"
          tag="button"
        >
          {userData?.profile_image ? (
            <img
              className="rounded-circle header-profile-user me-2"
              src={userData.profile_image}
              alt={displayName}
            />
          ) : (
            <div
              className="rounded-circle d-flex align-items-center justify-content-center fw-bold me-2"
              style={{
                width: "36px",
                height: "36px",
                backgroundColor: '#1e222d',
                color: '#F8CA15',
                fontSize: "13px",
                border: '1.5px solid #F8CA15'
              }}
            >
              {getInitials(displayName)}
            </div>
          )}
          <div className="d-none d-xl-flex flex-column text-start me-1">
            <span className="font-weight-semibold text-dark font-size-13 line-height-1 mb-1">
              {displayName}
            </span>
            <span className="text-muted font-size-11">
              {roleBadge.label}
            </span>
          </div>
          <i className="mdi mdi-chevron-down d-none d-xl-inline-block ms-1 text-muted"/>
        </DropdownToggle>

        <DropdownMenu className="dropdown-menu-end shadow border-0" style={{ minWidth: "240px" }}>
          {/* Header Card inside Dropdown */}
          <div className="p-3 bg-light rounded-top border-bottom">
            <div className="d-flex align-items-center">
              <div
                className="rounded-circle d-flex align-items-center justify-content-center fw-bold me-2"
                style={{
                  width: "40px",
                  height: "40px",
                  backgroundColor: '#1e222d',
                  color: '#F8CA15',
                  fontSize: "14px",
                  border: '1.5px solid #F8CA15'
                }}
              >
                {getInitials(displayName)}
              </div>
              <div className="overflow-hidden">
                <h6 className="mb-0 text-truncate font-size-14 fw-bold text-dark">{displayName}</h6>
                {displayEmail && <p className="mb-0 text-truncate font-size-12 text-muted">{displayEmail}</p>}
              </div>
            </div>
            <div className="d-flex align-items-center gap-1 mt-2">
              <Badge color={`soft-${roleBadge.color}`} className={`text-${roleBadge.color} font-size-11 fw-semibold`}>
                {roleBadge.label}
              </Badge>
              {clinicName && (
                <Badge color="soft-secondary" className="text-secondary font-size-11 fw-normal text-truncate" style={{ maxWidth: "140px" }}>
                  <i className="bx bx-clinic me-1 font-size-10" />
                  {clinicName}
                </Badge>
              )}
            </div>
          </div>

          <Link to="/profile" className="dropdown-item py-2">
            <i className="bx bx-user font-size-16 align-middle me-2 text-primary"/>
            <span>{props.t("My Profile")}</span>
          </Link>
          <Link to="/subscription" className="dropdown-item py-2">
            <i className="bx bx-credit-card font-size-16 align-middle me-2 text-success"/>
            <span>{props.t("Subscription & Billing")}</span>
          </Link>
          {isSuperAdminUser() && (
            <Link to="/superadmin" className="dropdown-item py-2">
              <i className="bx bx-crown font-size-16 align-middle me-2 text-warning"/>
              <span>{props.t("SuperAdmin Portal")}</span>
            </Link>
          )}
          <Link to="/lock-screen" className="dropdown-item py-2">
            <i className="bx bx-lock font-size-16 align-middle me-2 text-muted"/>
            <span>{props.t("Lock Screen")}</span>
          </Link>
          <div className="dropdown-divider my-1"/>
          <Link to="/logout" className="dropdown-item text-danger py-2">
            <i className="bx bx-power-off font-size-16 align-middle me-2"/>
            <span>{props.t("Logout")}</span>
          </Link>
        </DropdownMenu>
      </Dropdown>
    </React.Fragment>
  );
};

ProfileMenu.propTypes = {
  success: PropTypes.any,
  t: PropTypes.any
};

const mapStatetoProps = state => {
  const { error, success } = state.Profile;
  return { error, success };
};

export default withRouter(
  connect(mapStatetoProps, {})(withTranslation()(ProfileMenu))
);
